import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { createPortal } from 'react-dom';

import useGetEntitlements from '../../hooks/dashboard/subscription/useGetEntitlements';
import { useAppSelector } from '../../redux/store';
import { SUBSCRIPTIONS_PLANS } from '../../routes/routeConstants';
import FeatureGateModal from './FeatureGateModal';
import { SUBSCRIPTION_GATE_EVENT, SUBSCRIPTION_USAGE_EVENT } from '../../utils/subscriptionEvents';

export type SubscriptionFeature = 'canPostJobs' | 'canApplyToJobs' | 'canUseFindLocum' |
  'canReceiveJobAlerts' | 'canReceiveJobInvites';
const labels: Record<SubscriptionFeature, string> = {
  canPostJobs: 'Post Jobs', canApplyToJobs: 'Apply to Jobs', canUseFindLocum: 'Find a Locum',
  canReceiveJobAlerts: 'Job Alerts', canReceiveJobInvites: 'Job Invites',
};
interface Prompt { feature: SubscriptionFeature; message?: string; checkFailed?: boolean }
const AccessContext = createContext<{
  requireFeature: (feature: SubscriptionFeature) => Promise<boolean>;
} | null>(null);

export const useSubscriptionAccess = () => {
  const context = useContext(AccessContext);
  if (!context) throw new Error('Subscription actions require SubscriptionAccessProvider');
  return context;
};

export const SubscriptionAccessProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { getEntitlements } = useGetEntitlements();
  const entitlements = useAppSelector(state => state.subscription.entitlements);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const close = useCallback(() => setPrompt(null), []);
  const upgrade = useCallback(() => navigate(`/${SUBSCRIPTIONS_PLANS}`), [navigate]);

  const requireFeature = useCallback(async (feature: SubscriptionFeature) => {
    const current = await getEntitlements();
    if (!current) {
      setPrompt({ feature, checkFailed: true });
      return false;
    }
    if (!current[feature]) {
      const quota = feature === 'canPostJobs' ? current.usage?.jobPosts
        : feature === 'canApplyToJobs' ? current.usage?.jobApplications : undefined;
      setPrompt({ feature, message: quota && quota.limit !== 0 && quota.remaining === 0
        ? `Your monthly allowance is used (${quota.used}/${quota.limit}). Upgrade or wait until the next allowance reset.`
        : undefined });
      return false;
    }
    return true;
  }, [getEntitlements]);

  useEffect(() => {
    const onGate = (event: Event) => {
      const detail = (event as CustomEvent<Prompt>).detail;
      if (detail && detail.feature in labels) setPrompt(detail);
    };
    const refresh = () => { void getEntitlements(); };
    window.addEventListener(SUBSCRIPTION_GATE_EVENT, onGate);
    window.addEventListener(SUBSCRIPTION_USAGE_EVENT, refresh);
    return () => {
      window.removeEventListener(SUBSCRIPTION_GATE_EVENT, onGate);
      window.removeEventListener(SUBSCRIPTION_USAGE_EVENT, refresh);
    };
  }, [getEntitlements]);

  const contextValue = useMemo(() => ({ requireFeature }), [requireFeature]);
  return <AccessContext.Provider value={contextValue}>
    {children}
    {prompt ? createPortal(<FeatureGateModal
      isOpen onClose={close} onUpgrade={upgrade} feature={prompt.feature}
      featureLabel={labels[prompt.feature]} entitlements={entitlements}
      message={prompt.message} checkFailed={prompt.checkFailed}
      onRetry={async () => { if (await requireFeature(prompt.feature)) close(); }}
    />, document.getElementById('modal-root') || document.body) : null}
  </AccessContext.Provider>;
};
